import React, { useCallback, useState } from 'react';
import { withStyles } from '@material-ui/styles';
import styles from '../styles/informationSection';
import { translate } from '../../../i18n/I18nProvider';
import CheckboxRow from './CheckboxRow';
import AnswerPhones from './AnswerPhones';
import CustomColorButton from '../../../partials/customButtons/CustomColorButton';
import flowRight from 'lodash.flowright';
import withSettings from '../../../utils/withSettings';
import DataCheckboxRow from '../../../partials/dataBox/DataCheckboxRow';
import { TextField } from '@material-ui/core';
import Optional from '../../../utils/optional';
import AvailableNumbers from './AvailableNumbers';
import { connect } from 'react-redux';
import { isDefined } from '../../../utils/isDefined';

const EMPTY_PHONE_PLACE = {
  type: 'MOBILE',
  full_caller_id: false,
  enable_pbx: false,
  active: false,
  profile: 'Standard',
  client: 'STANDARD'
};

const AnsweringPlaces = ({
  classes,
  form,
  settings,
  licenses,
  numberErrors,
  availableNumbers,
  setNumberErrors
}) => {
  const [addPhoneBoxVisible, setAddPhoneBoxVisible] = useState(false);
  const [newNumber, setNewNumber] = useState('');

  const {
    values: { answer_places = [] },
    setFieldValue
  } = form;

  const getFilteredAvailableNumbers = useCallback(() => {
    const usedNumbers = answer_places
      .filter(place => place.type === 'MOBILE' && place.value)
      .map(place => place.value);

    const phoneLineNumbers = [];
    const { phoneNumbers = {} } = form.values;

    Object.keys(phoneNumbers).forEach(lineKey => {
      if (lineKey === 'faxLine') {
        return;
      }

      const line = phoneNumbers[lineKey];

      if (
        line?.value &&
        line.value.trim() !== '' &&
        line?.number_type &&
        line.number_type.includes('MOBILE')
      ) {
        phoneLineNumbers.push({
          value: line.value,
          label: line.value,
          source: 'phoneLine'
        });
      }
    });

    const combinedNumbers = [...availableNumbers];

    phoneLineNumbers.forEach(phoneLineNumber => {
      const alreadyExists = combinedNumbers.some(
        number => number.value === phoneLineNumber.value
      );
      if (!alreadyExists) {
        combinedNumbers.push(phoneLineNumber);
      }
    });

    return combinedNumbers.filter(
      number => !usedNumbers.includes(number.value)
    );
  }, [answer_places, availableNumbers, form.values]);

  const handleTextfieldBlur = useCallback(
    () => {
      delete numberErrors.newNumber;
      setNumberErrors({ ...numberErrors });
      const filteredNumbers = getFilteredAvailableNumbers();
      const exists = filteredNumbers.some(n => n.value === newNumber);

      if (!exists && newNumber) {
        setNumberErrors({
          ...numberErrors,
          newNumber: 'This number is not available'
        });
      } else {
        delete numberErrors.newNumber;
        setNumberErrors({ ...numberErrors });
      }
    },
    // eslint-disable-next-line
    [newNumber, numberErrors, setNumberErrors, getFilteredAvailableNumbers]
  );

  const changevalue = (type, value) => {
    if (type !== 'MOBILE') {
      if (value === true) {
        const updatedValues = [...answer_places, { type }];
        setFieldValue('answer_places', updatedValues);
      } else {
        const updatedValues = answer_places.filter(
          place => place.type !== type
        );
        setFieldValue('answer_places', updatedValues);
      }
    }
  };

  const onNumberSelected = useCallback(
    selectedNumber => {
      delete numberErrors.newNumber;
      setNumberErrors({ ...numberErrors });

      setNewNumber(selectedNumber);
    },

    [setNewNumber, numberErrors, setNumberErrors]
  );

  return (
    <div className={classes.root}>
      <div className={classes.label}>{translate('USER.ANSWER_PLACES')}</div>
      <div className={classes.container}>
        <div className={classes.smallLabel}>Softphone</div>
        <CheckboxRow
          label={translate('USER.ENABLE_SOFTPHONE')}
          type="SOFT"
          values={answer_places}
          changeValue={changevalue}
        />
        <DataCheckboxRow
          label={translate('USER.ENABLE_WEBSOFTPHONE')}
          name="web_softphone"
          disabled={
            isDefined(licenses?.webSoftphone) &&
            licenses?.webSoftphone === false
          }
          {...form}
        />
        <div className={classes.smallLabel}>
          {translate('USER.MOBILE_PHONES')}
        </div>
        <AnswerPhones data={answer_places} setFieldValue={setFieldValue} />
        {addPhoneBoxVisible && (
          <div className={classes.addNumberBox}>
            <div className={classes.addPhoneBox}>
              <div className={classes.textField}>
                <TextField
                  variant="outlined"
                  margin="dense"
                  value={Optional(newNumber).or('')}
                  onChange={e => setNewNumber(e.target.value)}
                  onBlur={handleTextfieldBlur}
                  error={!!numberErrors?.newNumber}
                  helperText={numberErrors?.newNumber}
                />
              </div>
              <CustomColorButton
                size="small"
                backgroundColor={settings?.colors.modal_header}
                color="#FFF"
                onClick={() => {
                  const isNumberAlreadyUsed = answer_places.some(
                    place =>
                      place.type === 'MOBILE' && place.value === newNumber
                  );

                  if (isNumberAlreadyUsed) {
                    setNumberErrors({
                      ...numberErrors,
                      newNumber: 'This number is already in use'
                    });
                    return;
                  }

                  setFieldValue('answer_places', [
                    ...answer_places,
                    { ...EMPTY_PHONE_PLACE, value: newNumber }
                  ]);
                  setAddPhoneBoxVisible(false);
                  setNewNumber('');
                  delete numberErrors.newNumber;
                  setNumberErrors({ ...numberErrors });
                }}
                disabled={!newNumber || !!numberErrors?.newNumber}
              >
                {translate('BUTTON.ADD')}
              </CustomColorButton>
              <CustomColorButton
                size="small"
                backgroundColor="inherit"
                color={settings?.colors.modal_header}
                onClick={() => {
                  setAddPhoneBoxVisible(false);
                  setNewNumber('');
                  delete numberErrors.newNumber;
                  setNumberErrors({ ...numberErrors });
                }}
              >
                {translate('BUTTON.CANCEL')}
              </CustomColorButton>
            </div>
            <AvailableNumbers
              availableNumbers={getFilteredAvailableNumbers()}
              setSelectedNumber={selected => onNumberSelected(selected.value)}
              excludeNumbers={answer_places
                .filter(place => place.type === 'MOBILE' && place.value)
                .map(place => place.value)}
            />
          </div>
        )}
        <CustomColorButton
          size="small"
          backgroundColor={settings?.colors.modal_header}
          color="#FFF"
          onClick={() => {
            setAddPhoneBoxVisible(true);
          }}
        >
          {translate('USER.ADD_MOBILE')}
        </CustomColorButton>
        <DataCheckboxRow
          label={translate('USER.ENABLE_MOBILE_VOIP')}
          name="mobile_voip"
          {...form}
        />
        <DataCheckboxRow
          label={translate('USER.ENABLE_LINE_IDENTIFICATION')}
          name="called_line_id"
          {...form}
        />
      </div>
    </div>
  );
};

const mapStatesToProps = ({ editUser }) => {
  return {
    licenses: editUser.licenses,
    availableNumbers: editUser.availableNumbers || []
  };
};

export default flowRight(
  withSettings,
  connect(mapStatesToProps),
  withStyles(styles)
)(AnsweringPlaces);
